import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import callContentsListApi from '../apis/callContentsListApi';
import { AlertButton } from '../components/Templates/ElmerModalAlert';
import {
  ContentsListApiResponseParam,
  CourseInfo,
  ResumeStatus,
} from '../types/apis/contentsListApi';
import useAccessKey from './useAccessKey';
import useCheckTransition from './useCheckTransition';
import { PAGE_PATH_NAME } from '../utils/const';

// アラート種別
const LESSON_ALERT_TYPES = {
  // ログオフ状態
  INFO_LOGOFF: 'INFO_LOGOFF',
  // コンテンツ取得APIエラー
  FAILED_CONTENTS_LIST_API: 'FAILED_CONTENTS_LIST_API',
} as const;
export type LESSON_ALERT_TYPES =
  typeof LESSON_ALERT_TYPES[keyof typeof LESSON_ALERT_TYPES];

// アラートに関する処理
export type LessonAlert = {
  // アラートタイプ
  type: LESSON_ALERT_TYPES | null;
  // アラートタイトル
  alertTitle: string;
  // アラート文言
  alertText: string;
  // アラートボタン
  alertButtons: Array<AlertButton>;
  // トレーニング選択画面アラート設定処理
  changeLessonAlertType: (type: LESSON_ALERT_TYPES | null) => void;
};

// 本カスタムフックの返り値
export type UseContentsListValue = {
  // コース情報一覧
  contents: CourseInfo[];
  // センテンス再生状況
  resume: ResumeStatus | null;
  // APIコール中かどうか
  isCallingApi: boolean;
  // アラートに関する処理
  lessonAlert: LessonAlert;
  // アラートを閉じる
  onClickCloseModal: () => void;
};

const useContentsList = (): UseContentsListValue => {
  // state
  // アクセスキー
  const { accessKey } = useAccessKey();
  // コース情報一覧
  const [contents, setContents] = useState<CourseInfo[]>([]);
  // センテンス再生状況
  const [resume, setResume] = useState<ResumeStatus | null>(null);
  // APIコール中かどうか
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false);
  // アラート状態
  const [lessonAlertType, setLessonAlertType] =
    useState<LESSON_ALERT_TYPES | null>(null);

  // history
  const history = useHistory();

  // ログイン済みチェック処理
  const { allowTransition } = useCheckTransition();

  /**
   * トレーニング選択画面アラートを設定(nullは非表示)
   * @param type アラート種別
   */
  const changeLessonAlertType = (type: LESSON_ALERT_TYPES | null) => {
    setLessonAlertType(type);
  };

  /**
   * アラートを非表示にする
   */
  const onClickCloseModal = () => {
    setLessonAlertType(null);
  };

  // 初期処理
  useEffect(() => {
    // ログイン済みチェック処理
    if (!allowTransition()) {
      return;
    }

    // インジケータを表示
    setIsCallingApi(true);

    // コンテンツ一覧取得API呼び出し
    callContentsListApi({ accesskey: accessKey })
      .then((response: ContentsListApiResponseParam): void => {
        if (response.resultCode === 'INFO_LOGOFF') {
          throw LESSON_ALERT_TYPES.INFO_LOGOFF;
        } else if (response.resultCode !== 'OK') {
          throw LESSON_ALERT_TYPES.FAILED_CONTENTS_LIST_API;
        }

        setContents(response.contents);
        if (response.resume !== null) {
          setResume({
            number: response.resume.number,
            description: response.resume.description,
            trainingId: response.resume.trainingId,
            sentenceId: response.resume.sentenceId,
          });
        }
      })
      .catch((error) => {
        if (error === 'INFO_LOGOFF') {
          // 自動ログオフ通知ダイアログ表示
          changeLessonAlertType(LESSON_ALERT_TYPES.INFO_LOGOFF);
        } else {
          // コンテンツ一覧取得エラーアラート表示
          changeLessonAlertType(LESSON_ALERT_TYPES.FAILED_CONTENTS_LIST_API);
        }
      })
      .finally(() => {
        // インジケータ解除
        setIsCallingApi(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * アラートを非表示にしてシリアル入力画面に遷移する
   */
  const moveToInputSerial = useCallback(() => {
    changeLessonAlertType(null);

    // シリアル入力画面に遷移
    history.replace(PAGE_PATH_NAME.SERIAL_INPUT);
  }, [history]);

  const alertTitle = useMemo(() => {
    switch (lessonAlertType) {
      case LESSON_ALERT_TYPES.FAILED_CONTENTS_LIST_API:
        return 'コンテンツ一覧取得エラー';
      default:
        return '';
    }
  }, [lessonAlertType]);

  const alertText = useMemo(() => {
    switch (lessonAlertType) {
      case LESSON_ALERT_TYPES.FAILED_CONTENTS_LIST_API:
        return 'コンテンツ一覧取得時にエラーが発生しました。\nお手数ですが、時間をおいて再度お試しください。';
      default:
        return '';
    }
  }, [lessonAlertType]);

  const alertButtons = useMemo(() => {
    switch (lessonAlertType) {
      case LESSON_ALERT_TYPES.FAILED_CONTENTS_LIST_API:
        return [{ label: '閉じる', onClick: moveToInputSerial }];
      default:
        return [];
    }
  }, [lessonAlertType, moveToInputSerial]);

  return {
    contents,
    resume,
    isCallingApi,
    lessonAlert: {
      type: lessonAlertType,
      alertTitle,
      alertText,
      alertButtons,
      changeLessonAlertType,
    },
    onClickCloseModal,
  };
};
export default useContentsList;
